import React, { useCallback, useEffect, useRef, useState } from 'react';
import { trainingApi } from '../../lib/api';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { ChevronDown, ChevronUp, Copy, GripVertical, Loader2, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

export default function TemplateEditorDialog({ onClose }) {
  const [list, setList] = useState(null);
  const [currentId, setCurrentId] = useState(null);
  const [tpl, setTpl] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [newName, setNewName] = useState('');
  const [newBlank, setNewBlank] = useState(false);
  const [creating, setCreating] = useState(false);

  const loadList = useCallback((selectId) => {
    trainingApi.listTemplates()
      .then(r => {
        setList(r.data);
        setCurrentId(prev => selectId || prev || r.data[0]?.template_id || null);
      })
      .catch(e => { toast.error(e.response?.data?.detail || 'Errore caricamento template'); onClose(); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { loadList(); }, [loadList]);

  useEffect(() => {
    if (!currentId) return;
    setTpl(null);
    trainingApi.getTemplate(currentId)
      .then(r => setTpl(r.data))
      .catch(e => toast.error(e.response?.data?.detail || 'Template non trovato'));
  }, [currentId]);

  const setSection = (i, patch) => setTpl(t => ({
    ...t, sections: t.sections.map((s, j) => j === i ? { ...s, ...patch } : s),
  }));
  const setItem = (i, k, patch) => setSection(i, {
    items: tpl.sections[i].items.map((it, j) => j === k ? { ...it, ...patch } : it),
  });
  const moveItem = (i, k, dir) => {
    const items = [...tpl.sections[i].items];
    const j = k + dir;
    if (j < 0 || j >= items.length) return;
    [items[k], items[j]] = [items[j], items[k]];
    setSection(i, { items });
  };

  // Drag & drop libero: righe (dentro la stessa sezione) e intere sezioni.
  const dragRef = useRef(null); // {type:'item', sec, idx} | {type:'section', idx}
  const moveItemTo = (i, from, to) => {
    const items = [...tpl.sections[i].items];
    const [moved] = items.splice(from, 1);
    items.splice(to, 0, moved);
    setSection(i, { items });
  };
  const moveSectionTo = (from, to) => setTpl(t => {
    const sections = [...t.sections];
    const [moved] = sections.splice(from, 1);
    sections.splice(to, 0, moved);
    return { ...t, sections };
  });
  const moveSection = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= tpl.sections.length) return;
    moveSectionTo(i, j);
  };

  const createNew = async () => {
    if (!newName.trim()) { toast.error('Inserisci il nome del nuovo template'); return; }
    setCreating(true);
    try {
      const r = await trainingApi.createTemplate({
        name: newName.trim(),
        copy_from: newBlank ? null : currentId,
        blank: newBlank,
      });
      toast.success(newBlank
        ? `Template "${newName.trim()}" creato da zero`
        : `Template "${newName.trim()}" creato (copia di quello selezionato)`);
      setShowNew(false);
      setNewName('');
      setNewBlank(false);
      loadList(r.data.template_id);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Creazione fallita');
    } finally {
      setCreating(false);
    }
  };

  const deleteCurrent = async () => {
    if (!window.confirm(`Eliminare il template "${tpl?.name}"? Le schede già create non vengono toccate.`)) return;
    try {
      await trainingApi.deleteTemplate(currentId);
      toast.success('Template eliminato');
      setCurrentId(null);
      loadList();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Eliminazione fallita');
    }
  };

  const save = async () => {
    for (const s of tpl.sections) {
      if (!s.title.trim()) { toast.error('Ogni sezione deve avere un titolo'); return; }
      if (s.items.length === 0) { toast.error(`La sezione "${s.title}" non ha voci`); return; }
      if (s.items.some(it => !it.label.trim())) { toast.error(`Voce senza nome in "${s.title}"`); return; }
    }
    setSaving(true);
    try {
      await trainingApi.updateTemplate(currentId, {
        name: tpl.name || null,
        sections: tpl.sections.map(s => ({
          section_id: s.section_id || null, title: s.title.trim(),
          items: s.items.map(it => ({ item_id: it.item_id || null, label: it.label.trim(),
                                      hint_low: it.hint_low || null, hint_high: it.hint_high || null })),
        })),
      });
      toast.success('Template salvato — vale per le nuove schede');
      loadList();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Salvataggio fallito');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={v => { if (!v) onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="template-editor-dialog">
        <DialogHeader>
          <DialogTitle>Template schede di valutazione</DialogTitle>
          <DialogDescription>
            Le modifiche valgono per le <b>nuove</b> schede: quelle già create mantengono il template con cui sono nate.
          </DialogDescription>
        </DialogHeader>

        {list === null ? (
          <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
        ) : (
          <>
            {/* Selettore + azioni template */}
            <div className="flex items-end gap-2 flex-wrap bg-slate-50 border border-slate-200 p-3">
              <div className="flex-1 min-w-[220px]">
                <label className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">Template</label>
                <Select value={currentId || ''} onValueChange={setCurrentId}>
                  <SelectTrigger className="rounded-none mt-1 bg-white" data-testid="template-select"><SelectValue placeholder="Seleziona template" /></SelectTrigger>
                  <SelectContent>
                    {list.map(t => (
                      <SelectItem key={t.template_id} value={t.template_id}>
                        {t.name} ({t.sections_count} sezioni · {t.items_count} voci)
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button variant="outline" size="sm" className="h-9 gap-1.5 rounded-none" onClick={() => setShowNew(v => !v)} data-testid="template-new-btn">
                <Copy className="w-3.5 h-3.5" /> Nuovo template
              </Button>
              {list.length > 1 && (
                <Button variant="outline" size="sm" className="h-9 gap-1.5 rounded-none text-red-600 border-red-200" onClick={deleteCurrent} data-testid="template-delete-btn">
                  <Trash2 className="w-3.5 h-3.5" /> Elimina
                </Button>
              )}
              {showNew && (
                <div className="w-full flex items-center gap-2 pt-1 flex-wrap">
                  <Input value={newName} onChange={e => setNewName(e.target.value)}
                         placeholder="Nome del nuovo template (es. Scheda Lady Barber)"
                         className="h-9 rounded-none bg-white flex-1 min-w-[200px]" data-testid="template-new-name" />
                  <label className="flex items-center gap-1.5 text-xs text-slate-600 whitespace-nowrap cursor-pointer">
                    <input type="checkbox" checked={newBlank} onChange={e => setNewBlank(e.target.checked)}
                           data-testid="template-new-blank" />
                    Parti da zero (vuoto)
                  </label>
                  <Button size="sm" className="h-9 rounded-none" onClick={createNew} disabled={creating} data-testid="template-new-create">
                    {creating ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : null} {newBlank ? 'Crea vuoto' : 'Crea copia'}
                  </Button>
                </div>
              )}
            </div>

            {!tpl ? (
              <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
            ) : (
              <>
                <div>
                  <label className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">Nome template</label>
                  <Input value={tpl.name || ''} onChange={e => setTpl(t => ({ ...t, name: e.target.value }))}
                         className="rounded-none mt-1" data-testid="template-name" />
                </div>

                <div className="space-y-3">
                  {tpl.sections.map((sec, i) => (
                    <div key={sec.section_id || i} className="border border-slate-200 p-3 space-y-2 bg-slate-50/60" data-testid={`template-section-${i}`}
                         onDragOver={e => { if (dragRef.current?.type === 'section') e.preventDefault(); }}
                         onDrop={e => {
                           const d = dragRef.current;
                           if (d?.type === 'section') { e.preventDefault(); if (d.idx !== i) moveSectionTo(d.idx, i); dragRef.current = null; }
                         }}>
                      <div className="flex items-center gap-2">
                        <span draggable
                              onDragStart={e => { dragRef.current = { type: 'section', idx: i }; e.dataTransfer.effectAllowed = 'move'; }}
                              className="cursor-grab text-slate-300 hover:text-slate-600 shrink-0"
                              title="Trascina per riordinare la sezione" data-testid={`template-section-${i}-drag`}>
                          <GripVertical className="w-4 h-4" />
                        </span>
                        <div className="flex flex-col shrink-0">
                          <button type="button" disabled={i === 0}
                                  className="text-slate-400 hover:text-slate-800 disabled:opacity-20"
                                  onClick={() => moveSection(i, -1)} data-testid={`template-section-${i}-up`}>
                            <ChevronUp className="w-3.5 h-3.5" />
                          </button>
                          <button type="button" disabled={i === tpl.sections.length - 1}
                                  className="text-slate-400 hover:text-slate-800 disabled:opacity-20"
                                  onClick={() => moveSection(i, 1)} data-testid={`template-section-${i}-down`}>
                            <ChevronDown className="w-3.5 h-3.5" />
                          </button>
                        </div>
                        <Input value={sec.title} onChange={e => setSection(i, { title: e.target.value })}
                               placeholder="Titolo sezione" className="h-8 rounded-none font-semibold uppercase text-xs"
                               data-testid={`template-section-${i}-title`} />
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500 shrink-0"
                                onClick={() => setTpl(t => ({ ...t, sections: t.sections.filter((_, j) => j !== i) }))}
                                data-testid={`template-section-${i}-remove`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                      <div className="space-y-1">
                        {sec.items.map((it, k) => (
                          <div key={it.item_id || k} className="flex items-center gap-1.5"
                               onDragOver={e => { const d = dragRef.current; if (d?.type === 'item' && d.sec === i) e.preventDefault(); }}
                               onDrop={e => {
                                 const d = dragRef.current;
                                 if (d?.type === 'item' && d.sec === i) {
                                   e.preventDefault(); e.stopPropagation();
                                   if (d.idx !== k) moveItemTo(i, d.idx, k);
                                   dragRef.current = null;
                                 }
                               }}>
                            <span draggable
                                  onDragStart={e => { dragRef.current = { type: 'item', sec: i, idx: k }; e.dataTransfer.effectAllowed = 'move'; }}
                                  className="cursor-grab text-slate-300 hover:text-slate-600 shrink-0"
                                  title="Trascina per riordinare" data-testid={`template-item-${i}-${k}-drag`}>
                              <GripVertical className="w-3.5 h-3.5" />
                            </span>
                            <div className="flex flex-col shrink-0">
                              <button type="button" disabled={k === 0}
                                      className="text-slate-400 hover:text-slate-800 disabled:opacity-20"
                                      onClick={() => moveItem(i, k, -1)} data-testid={`template-item-${i}-${k}-up`}>
                                <ChevronUp className="w-3.5 h-3.5" />
                              </button>
                              <button type="button" disabled={k === sec.items.length - 1}
                                      className="text-slate-400 hover:text-slate-800 disabled:opacity-20"
                                      onClick={() => moveItem(i, k, 1)} data-testid={`template-item-${i}-${k}-down`}>
                                <ChevronDown className="w-3.5 h-3.5" />
                              </button>
                            </div>
                            <Input value={it.label} onChange={e => setItem(i, k, { label: e.target.value })}
                                   placeholder="Voce da valutare" className="h-7 rounded-none text-sm bg-white"
                                   data-testid={`template-item-${i}-${k}`} />
                            <button type="button" className="text-red-400 hover:text-red-600 shrink-0"
                                    onClick={() => setSection(i, { items: sec.items.filter((_, j) => j !== k) })}
                                    data-testid={`template-item-${i}-${k}-remove`}>
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        ))}
                        <Button variant="outline" size="sm" className="h-7 rounded-none gap-1 text-xs"
                                onClick={() => setSection(i, { items: [...sec.items, { label: '' }] })}
                                data-testid={`template-section-${i}-add-item`}>
                          <Plus className="w-3 h-3" /> Aggiungi voce
                        </Button>
                      </div>
                    </div>
                  ))}
                  <Button variant="outline" size="sm" className="rounded-none gap-1.5"
                          onClick={() => setTpl(t => ({ ...t, sections: [...t.sections, { title: '', items: [{ label: '' }] }] }))}
                          data-testid="template-add-section">
                    <Plus className="w-3.5 h-3.5" /> Aggiungi sezione
                  </Button>
                </div>
              </>
            )}
          </>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Chiudi</Button>
          <Button onClick={save} disabled={saving || !tpl} data-testid="template-save-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : null} Salva template
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
